/**
 * Establishing a session before the application needs one.
 *
 * Two paths lead to a token, and which is tried first depends on where this
 * application is running. That is the whole design, so it is worth stating.
 *
 * **Embedded, the host is asked first.** This is how the builder ships: a
 * frame inside ESG or EPM, which already holds a session and can relay a
 * token over `postMessage`. Asking the portal instead would mean a hidden
 * frame running `prompt=none` against its authorize endpoint, and a nested
 * cross-site frame needs third-party cookies to reach the portal's session
 * cookie. Safari refuses those outright and Chrome blocks them in several
 * ordinary modes, so that path is not merely slower, it is one the browser
 * may simply not allow. It is still tried, second, for the hosts that have
 * not been taught to relay anything.
 *
 * **Standalone, the portal is asked first.** Nothing is embedding us, so the
 * silent path works normally, and a redirect is available when it does not.
 *
 * Neither case has a fallback the other can borrow. Embedded, a redirect is
 * out of the question: navigating would take the host page away with us.
 * Standalone, there is no host to ask.
 *
 * The host listener is attached first and synchronously, before either path
 * runs. A host that announces its theme milliseconds after the frame loads
 * will have finished doing so before a slower start, and the page then sits in
 * the wrong theme until something else prompts it.
 *
 * Nobody is ever asked to press a button to begin. Standalone, a browser with
 * no portal session is sent to the portal, because that is the only thing that
 * could have happened next anyway: an intervening screen offering to do it is
 * a click that carries no decision. The screen kept for the case where the
 * redirect itself fails is not that screen; it is the one that says the portal
 * could not be reached, which is a different fact and a useful one.
 */

import { watch } from 'vue'

import { getMe } from '@/api/access'
import { onTokenExpired, useTokenSource } from '@/api/http'
import { config } from '@/app/config'
import { hostBridge, isEmbedded } from '@/app/host-bridge'
import type { Identity } from '@/auth/claims'
import { chosenDevAccount } from '@/auth/devAccount'
import { signInSilently, signInWithRedirect, userManager } from '@/auth/oidc'
import { t } from '@/i18n'
import { useSessionStore } from '@/stores/session'

/** Routes the identity provider redirects to, which must not be interrupted. */
const CALLBACK_PATHS = new Set(['/callback', '/silentrenew'])

/**
 * How long to give the host to relay a token before trying the portal.
 *
 * A host that relays at all answers `requestToken` immediately, so this is
 * long enough to cover a slow frame start and short enough that a host which
 * will never answer does not hold up the fallback behind it.
 */
const HOST_TOKEN_WAIT_MS = 3_000

/**
 * Who the API says the chosen development account is, or the fixed development
 * identity when none is chosen or the API cannot say.
 */
async function chosenIdentity(): Promise<Identity | undefined> {
  if (!chosenDevAccount()) return undefined
  try {
    const me = await getMe()
    return {
      id: me.id,
      username: me.username,
      email: me.email,
      name: me.name || me.username,
      roles: new Set(me.profiles),
      expiresAt: 0,
    }
  } catch {
    return undefined
  }
}

/**
 * Get a session, however it can be had.
 *
 * Returns whether one was established. A caller that gets `false` is running
 * without a session, which every request will then be refused for, and should
 * say so rather than showing an empty workspace.
 */
export async function boot(): Promise<boolean> {
  const session = useSessionStore()

  // Wire the HTTP client to the session before anything can make a request.
  useTokenSource(() => session.currentToken())
  onTokenExpired(() => {
    // The API refused the token. Renewing is worth one attempt: the ordinary
    // cause is a token that expired mid-request.
    void renew()
  })

  // Running without an identity provider. Nothing is contacted, no token is
  // obtained, and the API is in the same state so it identifies the caller the
  // same way. Announced loudly, because an application with no access control
  // should never be quiet about it.
  if (!config.auth.enabled) {
    console.warn('[auth] authentication is switched off: running as a fixed development identity')
    session.signedInWithoutAuthentication(await chosenIdentity())
    session.settled()
    return true
  }

  // These pages exist to complete a sign-in. Booting one would race the very
  // exchange it is there to finish. Left unsettled on purpose: the callback
  // view says what is happening, and it is happening.
  if (CALLBACK_PATHS.has(window.location.pathname)) return false

  listenToHost()

  return isEmbedded() ? await bootEmbedded() : await bootStandalone()
}

/**
 * Get a session inside a host frame.
 *
 * The host first, then the portal, then an explanation. There is deliberately
 * no redirect at the end of this: taking the host page away to sign in would
 * discard whatever the person was doing in the application around us.
 */
async function bootEmbedded(): Promise<boolean> {
  const session = useSessionStore()

  hostBridge.requestToken()
  if (await waitForHostToken()) {
    session.settled()
    return true
  }

  // The host did not answer, or was never taught to. The portal may still, if
  // this browser is one that will carry its cookies into a nested frame.
  const result = await signInSilently()
  if (result.kind === 'signed-in') {
    session.signedIn(result.user.access_token)
    session.settled()
    watchRenewals()
    return true
  }

  session.problem = t('session.hostSilent')
  session.settled()
  return false
}

/**
 * Get a session with nothing embedding us.
 *
 * Reached by opening the application's address directly, which in the deployed
 * shape means somebody outside ESG or EPM. They are sent to the portal, and
 * nothing here decides whether they may come back: the portal decides that,
 * and the API decides it again on every request.
 */
async function bootStandalone(): Promise<boolean> {
  const session = useSessionStore()

  const result = await signInSilently()
  if (result.kind === 'signed-in') {
    session.signedIn(result.user.access_token)
    session.settled()
    watchRenewals()
    return true
  }

  // Straight to the portal: there is no session, no way to obtain one quietly,
  // and nothing to ask anybody.
  //
  // Nothing follows a successful call, since the browser has left the page by
  // then. Reaching the line after it means the redirect could not be started,
  // which in practice means the portal did not answer.
  try {
    await signInWithRedirect(window.location.pathname + window.location.search)
  } catch (error) {
    console.error('[auth] the portal could not be reached to sign in:', error)
    // No reason set: this is the ordinary failure, and the screen already
    // says it. A problem is for the cases the screen could not have guessed.
    session.settled()
  }
  return false
}

/**
 * Wait for the host to relay a token, up to a point.
 *
 * Resolves true if one arrived. The wait has to end somewhere: a host that
 * never answers must not leave a frame waiting forever with nothing behind it.
 */
function waitForHostToken(): Promise<boolean> {
  const session = useSessionStore()
  if (session.isSignedIn) return Promise.resolve(true)

  return new Promise((resolve) => {
    let timer = 0

    const stop = watch(
      () => session.isSignedIn,
      (signedIn) => {
        if (!signedIn) return
        window.clearTimeout(timer)
        stop()
        resolve(true)
      },
    )

    timer = window.setTimeout(() => {
      stop()
      resolve(false)
    }, HOST_TOKEN_WAIT_MS)
  })
}

/** Take what the host tells us. */
function listenToHost(): void {
  const session = useSessionStore()

  hostBridge.start()
  hostBridge.on((message) => {
    switch (message.kind) {
      case 'token':
        // Only when there is nothing better. A relayed token must never
        // displace one obtained from the portal directly.
        if (!session.isSignedIn || session.isExpired) session.signedIn(message.token)
        break

      case 'signed-out':
        session.signedOut()
        break

      case 'theme':
      case 'reporting-context':
        // Handled by whoever is interested; nothing to do for a session.
        break
    }
  })

  hostBridge.requestTheme()
  hostBridge.requestReportingContext()
}

/** Keep the session store in step with the OIDC client's own renewals. */
function watchRenewals(): void {
  const session = useSessionStore()

  userManager.events.addUserLoaded((user) => {
    session.signedIn(user.access_token)
  })

  userManager.events.addUserUnloaded(() => {
    session.signedOut()
  })

  userManager.events.addAccessTokenExpired(() => {
    void renew()
  })

  userManager.events.addSilentRenewError((error) => {
    console.warn('[auth] a silent renewal failed:', error.message)
  })
}

/** Ask for a fresh token. Quiet on failure: the caller cannot act on it. */
async function renew(): Promise<void> {
  const session = useSessionStore()
  const result = await signInSilently()
  if (result.kind === 'signed-in') {
    session.signedIn(result.user.access_token)
    return
  }
  session.signedOut()
  if (isEmbedded()) hostBridge.requestToken()
}
